import { cn, PaperPanel, Swipe, Tag } from "@scribenote/brian-design-system";

import type { FailureOutcome, PimsFailureIncident } from "./pimsAuditClient";

/**
 * The callout idiom: an opaque sheet with the status ink held far back over it
 * (`/8`), the same ink on the edge and on the bar down the left, and the outcome
 * word carrying the only wash. The tint is a transparency, so it needs paper to
 * borrow its hue from - hence PaperPanel's opaque `bg-card` underneath rather
 * than a tinted surface of its own, and no bright fill anywhere near the words.
 *
 * Spelled out per outcome rather than interpolated from a token name: Tailwind
 * only emits the classes it finds literally in the source.
 */
const OUTCOME_CHROME = {
  failed: {
    tint: "bg-ink-stopped/8",
    accent: "bg-ink-stopped",
    border: "border-ink-stopped/40",
  },
  succeeded_with_recovery: {
    tint: "bg-ink-flag/8",
    accent: "bg-ink-flag",
    border: "border-ink-flag/40",
  },
} satisfies Record<
  FailureOutcome,
  { tint: string; accent: string; border: string }
>;

/**
 * The task-level outcome callout for a PIMS write that failed or only completed after
 * retrying (WEB-3272): the audit's clinic-safe failure projection as a plain-language
 * "why", with the outcome said in a word under its highlighter and the failure category
 * tagged as the fact it is.
 *
 * The incident list stays visible whether or not a replay video exists: legacy/v1
 * chapters and incomplete step joins can't represent every incident on the replay rail.
 * Confidence is intentionally never rendered (v0 decision). Renders nothing without
 * both an outcome and a reason, so clean-success and legacy audits are untouched.
 */
interface Props {
  outcome: FailureOutcome | null;
  why: string | null;
  category: string | null;
  incidents: PimsFailureIncident[];
}

export function PimsOutcomeCallout({
  outcome,
  why,
  category,
  incidents,
}: Props) {
  if (!outcome || !why) return null;
  const failed = outcome === "failed";
  const chrome = OUTCOME_CHROME[outcome];
  return (
    <PaperPanel
      className={cn("relative overflow-hidden", chrome.border)}
      data-testid="pims-outcome-callout"
      data-intercom-target="pims-outcome-callout"
    >
      <span
        aria-hidden="true"
        className={cn("absolute inset-0", chrome.tint)}
      />
      <span
        aria-hidden="true"
        className={cn("absolute inset-y-0 left-0 w-1.5", chrome.accent)}
      />
      <div className="relative flex min-w-0 flex-col gap-1.5 py-2.5 pl-4 pr-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h3 className="min-w-0 font-display text-sm font-medium text-ink">
            {failed ? "Why I stopped" : "I got there, on the second try"}
          </h3>
          {/* The outcome in one word, marked: red for the write that stopped,
              green for the one that landed anyway. */}
          <Swipe
            tone={failed ? "stopped" : "working"}
            className="shrink-0 text-annotation-caps"
            data-testid="pims-outcome-word"
          >
            {failed ? "Stopped" : "Done"}
          </Swipe>
          {/* The category names what kind of trouble this was, which is a fact
              about the write, not its state - so it is a tag, never a second
              highlighter competing with the word beside it. */}
          {category ? (
            <Tag className="shrink-0" data-testid="pims-outcome-category">
              {category}
            </Tag>
          ) : null}
        </div>
        <p className="text-sm text-ink-body [overflow-wrap:anywhere]">{why}</p>
        {incidents.length > 0 ? (
          <ol className="list-decimal pl-5 text-sm text-ink-body marker:font-mono marker:text-annotation">
            {incidents.map((inc, i) => (
              <li key={i} data-testid={`pims-outcome-incident-${i}`}>
                <span className="font-medium text-ink">{inc.label}:</span>{" "}
                {inc.explanation}
              </li>
            ))}
          </ol>
        ) : null}
      </div>
    </PaperPanel>
  );
}
